import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, MailIcon, MessageCircleIcon, XIcon } from 'lucide-react';
import { mainNav, aboutLinks, resourceLinks } from '../../data/navigation';
import { courseGroups, courses } from '../../data/courses';
import { destinations } from '../../data/destinations';
import { contact } from '../../data/contact';
import { Logo } from './Logo';
import { ActionLink } from '../ui/ActionLink';
import { StatusBadge } from '../ui/StatusBadge';
import { destinationPath, programmePath } from '../../utils/paths';
import type { LinkItem, MenuId } from '../../types/content';
interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}
interface MenuGroup {
  label: string;
  links: (LinkItem & {
    comingSoon?: boolean;
  })[];
}
const menuGroups: Record<MenuId, MenuGroup[]> = {
  destinations: [{
    label: 'Year-round',
    links: destinations.filter(d => d.yearRound).map(d => ({
      label: d.name,
      to: destinationPath(d, 'year-round')
    }))
  }, {
    label: 'Seasonal',
    links: destinations.filter(d => d.seasonal).map(d => ({
      label: d.name,
      to: destinationPath(d, 'seasonal')
    }))
  }],
  programmes: courseGroups.map(group => ({
    label: group.label,
    links: courses.filter(c => c.group === group.id).map(c => ({
      label: c.name,
      to: programmePath(c.slug)
    }))
  })),
  about: [{
    label: 'ES World',
    links: aboutLinks
  }, {
    label: 'Resources',
    links: resourceLinks
  }]
};
export function MobileMenu({
  open,
  onClose
}: MobileMenuProps) {
  const [expanded, setExpanded] = useState<MenuId | null>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  const subLink = 'flex items-center gap-2 py-2 type-card-desc text-body transition-colors duration-150 hover:text-ink';
  return <AnimatePresence data-id="element-610" data-mp="3cldtalgb8cje">
      {open ? <>
          <motion.div className="fixed inset-0 z-50 bg-black/40 lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-611" data-mp="37poqm98zufiv" />
          <motion.div role="dialog" aria-modal="true" aria-label="Main menu" className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col bg-white lg:hidden" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-612" data-mp="2tpnm8jtispyn">
            <div className="flex h-[72px] items-center justify-between border-b border-line px-5" data-id="element-613" data-mp="bqnyiproxjjv">
              <Logo data-id="element-614" data-mp="gixqscga2vpb" />
              <button type="button" onClick={onClose} aria-label="Close menu" className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors duration-150 hover:bg-mist" data-id="element-615" data-mp="gk8w4gpdgbij">
                <XIcon className="h-6 w-6" data-id="element-616" data-mp="uign87zq8ils" />
              </button>
            </div>
            <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-5 py-4" data-id="element-617" data-mp="zfywy0lkj1uu">
              <ul className="divide-y divide-line" data-id="element-618" data-mp="li1zz337ctfn">
                {mainNav.map(item => {
              const menu = item.menu;
              return <li key={item.label} data-id="element-619" data-mp="367hmvmzavy00">
                      {menu ? <>
                          <button type="button" className="flex w-full items-center justify-between py-4 text-left type-menu text-ink" aria-expanded={expanded === menu} onClick={() => setExpanded(expanded === menu ? null : menu)} data-id="element-620" data-mp="3b6kqr43jny6e">
                            {item.label}
                            <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ${expanded === menu ? 'rotate-180' : ''}`} data-id="element-621" data-mp="3cpcpgaoixm44" />
                          </button>
                          {expanded === menu ? <div className="pb-4" data-id="element-622" data-mp="48rckisru8vq">
                              {menuGroups[menu].map(group => <div key={group.label} className="mb-3" data-id="element-623" data-mp="3vg3vb29ayudr">
                                  <p className="pt-2 type-label text-muted" data-id="element-624" data-mp="2oz8o69yt5yga">{group.label}</p>
                                  {group.links.map(link => <Link key={link.to} to={link.to} className={subLink} onClick={onClose} data-id="element-625" data-mp="2jykn0vgv818c">
                                      {link.label}
                                      {link.comingSoon ? <StatusBadge data-id="element-626" data-mp="14i96gajad42j">Coming soon</StatusBadge> : null}
                                    </Link>)}
                                </div>)}
                              <Link to={item.to} className="mt-2 block py-2 type-button font-bold text-brand" onClick={onClose} data-id="element-627" data-mp="1dasz8ucwcefg">
                                Open {item.label} overview
                              </Link>
                            </div> : null}
                        </> : <Link to={item.to} className="block py-4 type-menu text-ink" onClick={onClose} data-id="element-628" data-mp="1w8vu3ps4l5l6">
                          {item.label}
                        </Link>}
                    </li>;
            })}
              </ul>
            </nav>
            <div className="space-y-3 border-t border-line p-5" data-id="element-629" data-mp="15509rpx22goz">
              <ActionLink to="/contact" className="w-full" data-id="element-630" data-mp="157csi5an0v33">
                Make an enquiry
              </ActionLink>
              <div className="flex gap-3" data-id="element-631" data-mp="1e5yjmou6difh">
                <a href={contact.whatsappLink} target="_blank" rel="noreferrer" className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[15px] bg-success type-button font-bold text-ink transition-colors duration-150 hover:bg-success-hover" data-id="element-632" data-mp="1e6zwmyur4ezu">
                  <MessageCircleIcon className="h-5 w-5" data-id="element-633" data-mp="1j3708kzodeem" /> WhatsApp
                </a>
                <a href={contact.emailLink} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[15px] border border-ink type-button font-bold text-ink transition-colors duration-150 hover:bg-mist" data-id="element-634" data-mp="1xw0gi8mrtykc">
                  <MailIcon className="h-5 w-5" data-id="element-635" data-mp="21wu3mzse39c6" /> Email
                </a>
              </div>
            </div>
          </motion.div>
        </> : null}
    </AnimatePresence>;
}